// Shared compose-URL builders for opening a pre-filled draft in the agent's own
// mail client. Pitcherly NEVER sends mail — these only open Gmail/Outlook/mailto
// with the recipient in `to` and the body pre-filled; the agent reviews & sends.
//
// Used by both single-send (renderSendEmail) and batch "Send to multiple" so URL
// construction lives in exactly one place. Every param is URL-encoded, and the
// body is clamped to a safe length per provider with a truncation notice so a
// long CMA/listing never silently breaks the compose link (a "Copy full email"
// button supplies the untruncated text).

const enc = (s) => encodeURIComponent(s == null ? "" : String(s));

// Practical compose-URL length ceilings (chars) before clients truncate/refuse.
// Gmail tolerates ~8k; mailto/Outlook deeplinks break closer to ~2k.
const URL_LIMITS = { gmail: 7500, outlook: 1800, mailto: 1800 };

// Reserve headroom for the scheme, host, to=, subject=, and param names.
const OVERHEAD = 220;

const TRUNCATE_NOTICE = "\n\n[Message truncated — use “Copy full email” to paste the rest.]";

// Clamp `body` so the encoded URL stays under the provider limit. Returns the
// (possibly shortened) body plus a `truncated` flag for the UI.
export function clampBody(body, provider = "mailto") {
  const b = body == null ? "" : String(body);
  const budget = (URL_LIMITS[provider] || URL_LIMITS.mailto) - OVERHEAD;
  if (enc(b).length <= budget) return { body: b, truncated: false };
  // Binary-search the largest char count whose encoded form (plus the notice)
  // fits the budget.
  let lo = 0, hi = b.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (enc(b.slice(0, mid) + TRUNCATE_NOTICE).length <= budget) lo = mid;
    else hi = mid - 1;
  }
  return { body: b.slice(0, lo).trimEnd() + TRUNCATE_NOTICE, truncated: true };
}

export function buildGmailUrl({ to = "", subject = "", body = "", authuser = "" }) {
  const { body: b, truncated } = clampBody(body, "gmail");
  const url =
    `https://mail.google.com/mail/u/?authuser=${enc(authuser)}&view=cm` +
    `&to=${enc(to)}&su=${enc(subject)}&body=${enc(b)}`;
  return { url, truncated };
}

export function buildOutlookUrl({ to = "", subject = "", body = "" }) {
  const { body: b, truncated } = clampBody(body, "outlook");
  const url =
    `https://outlook.live.com/mail/0/deeplink/compose?` +
    `to=${enc(to)}&subject=${enc(subject)}&body=${enc(b)}`;
  return { url, truncated };
}

export function buildMailtoUrl({ to = "", subject = "", body = "" }) {
  const { body: b, truncated } = clampBody(body, "mailto");
  const url = `mailto:${enc(to)}?subject=${enc(subject)}&body=${enc(b)}`;
  return { url, truncated };
}

// Substitute personalization tokens for a single contact. Supports {first_name}
// and {full_name} (and {firstName}/{fullName} aliases). Unknown tokens are left
// as-is. Used by batch send to address each draft individually.
export function personalize(text, contact = {}) {
  const first = (contact.firstName || "").trim();
  const last = (contact.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return String(text == null ? "" : text)
    .replace(/\{first_name\}/gi, first)
    .replace(/\{firstName\}/g, first)
    .replace(/\{full_name\}/gi, full)
    .replace(/\{fullName\}/g, full);
}
